import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaSearch,
  FaFilter,
  FaSpinner,
  FaFileInvoiceDollar,
  FaEllipsisV,
  FaPlus,
  FaEye,
  FaEdit,
  FaCheckCircle,
} from "react-icons/fa";

import apiRequest from "../services/api";
import InvoiceModal from "../components/InventoryAction";

const InventoryMovement = ({ invoiceType }) => {
  const [invoices, setInvoices] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [selectedType, setSelectedType] = useState(invoiceType || "ALL");

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const size = 15;

  const [activeInvoice, setActiveInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState({ top: 0, left: 0 });
  const menuContainerRef = useRef(null);

  useEffect(() => {
    if (invoiceType) setSelectedType(invoiceType);
  }, [invoiceType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        event.target.closest(".ellipsis-trigger-btn") ||
        (menuContainerRef.current &&
          menuContainerRef.current.contains(event.target))
      ) {
        return;
      }
      setActiveMenuId(null);
    };

    if (activeMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenuId]);

  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const fetchInvoicesList = useCallback(
    async (targetPage, isNewSearch = false) => {
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          keyword: keyword.trim(),
          type: selectedType,
          page: targetPage,
          size,
        }).toString();

        const response = await apiRequest(`invoices?${queryParams}`);
        const content = response?.data?.content || [];
        const last = response?.data?.last ?? true;

        setInvoices((prev) => (isNewSearch ? content : [...prev, ...content]));
        setHasMore(!last && content.length === size);
      } catch (err) {
        console.error(err);
        if (isNewSearch) setInvoices([]);
      } finally {
        setIsLoading(false);
      }
    },
    [keyword, selectedType]
  );

  const handleFinalize = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to approve this action?\n\nThis will update stock and post any required accounting entries. This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      await apiRequest(`invoices/inventory/${id}/finalize-action`, {
        method: "PUT",
      });
      refreshRegistryGrid();
      setActiveMenuId(null);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setPage(0);
    fetchInvoicesList(0, true);
  }, [keyword, selectedType, fetchInvoicesList]);

  useEffect(() => {
    if (page > 0) fetchInvoicesList(page, false);
  }, [page, fetchInvoicesList]);

  const handleOpenActionModal = (invoice) => {
    setActiveInvoice(invoice);
    setActiveMenuId(null);
    setIsModalOpen(true);
  };

  const refreshRegistryGrid = () => {
    setPage(0);
    fetchInvoicesList(0, true);
  };

  const getStatusBadgeStyles = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20";
      case "DRAFT":
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-white/5";
    }
  };

  const getHeaderTitle = () => {
    switch (selectedType) {
      case "ALL":
        return "Inventory Movement";
      case "TRANSFER":
        return "Transfer Records";
      case "ADJUSTMENT":
        return "Adjustment Records (In & Out)";
      case "ADJUSTMENT_IN":
        return "Stock Adjustments (In)";
      case "ADJUSTMENT_OUT":
        return "Stock Adjustments (Out)";
      case "DAMAGE":
        return "Damage Stock Records";
      default:
        return `${selectedType}`;
    }
  };

  const getWorkflow = (invoice) => {
    const status = (invoice?.status || "").toUpperCase();
    return {
      canEdit: status === "DRAFT",
      canFinalize: status === "DRAFT",
    };
  };

  const getCreationTypeFallback = () => {
    if (selectedType === "ALL" || selectedType === "ADJUSTMENT") {
      return "TRANSFER";
    }
    return selectedType;
  };

  const menuButtonClass =
    "w-full text-left px-3 py-2 rounded-lg text-xs transition flex items-center gap-2 font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10";

  return (
    <div
      className="min-h-full p-4 md:p-8 font-sans
      bg-slate-50 text-slate-800
      dark:bg-slate-950 dark:text-slate-100"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER */}
        <div
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl shadow-sm border backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl"
        >
          <div className="flex items-center gap-3.5">
            <div
              className="p-3 rounded-xl shadow-inner border
              bg-gradient-to-br from-amber-100 to-orange-50 border-amber-200 text-amber-600
              dark:from-amber-500/20 dark:to-orange-500/10 dark:border-amber-500/30 dark:text-amber-400"
            >
              <FaFileInvoiceDollar className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                {getHeaderTitle()}
              </h1>
              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                  {selectedType}
                </span>
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleOpenActionModal(null)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition shadow-lg shadow-amber-600/10
              bg-amber-600 hover:bg-amber-500 text-white"
          >
            <FaPlus /> New Transaction
          </button>
        </div>

        {/* FILTERS */}
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-xl border backdrop-blur-sm
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          <div className="relative col-span-1 sm:col-span-2">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaSearch className="text-xs" />
            </span>
            <input
              type="text"
              placeholder="Search reference numbers or notes..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full rounded-xl pl-9 pr-4 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
            />
          </div>

          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaFilter className="text-[10px]" />
            </span>
            <select
              value={selectedType}
              disabled={!!invoiceType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full rounded-xl pl-8 pr-4 py-2.5 text-xs transition appearance-none font-bold focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white
                disabled:opacity-50 disabled:text-amber-600 dark:disabled:text-amber-400"
            >
              <option value="ALL">All Movements</option>
              <option value="TRANSFER">Transfers</option>
              <option value="ADJUSTMENT">Adjustments (Combined)</option>
              <option value="ADJUSTMENT_IN">Adjustment In (+)</option>
              <option value="ADJUSTMENT_OUT">Adjustment Out (-)</option>
              <option value="DAMAGE">Damaged Goods</option>
            </select>
          </div>
        </div>

        {/* TABLE */}
        <div
          className="border rounded-2xl shadow-sm backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl"
        >
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr
                  className="border-b text-[10px] font-bold tracking-wider uppercase
                  bg-slate-50 border-slate-200 text-slate-500
                  dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400"
                >
                  <th className="px-5 py-4">Reference</th>
                  <th className="px-4 py-4">Type</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4 text-right">Total Value (ETB)</th>
                  {(selectedType === "TRANSFER" || selectedType === "ALL") && (
                    <>
                      <th className="px-4 py-4">From</th>
                      <th className="px-4 py-4">To</th>
                    </>
                  )}
                  <th className="px-4 py-4 text-right">Description / Note</th>
                  <th className="px-5 py-4 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody
                className="divide-y font-mono text-xs
                divide-slate-100 text-slate-600
                dark:divide-white/5 dark:text-slate-300"
              >
                {invoices.length === 0 && !isLoading ? (
                  <tr>
                    <td
                      colSpan={
                        selectedType === "TRANSFER" || selectedType === "ALL"
                          ? 8
                          : 6
                      }
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No records match the current filter criteria.
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice, index) => {
                    const isLastRow = invoices.length === index + 1;
                    return (
                      <tr
                        key={invoice.id}
                        ref={isLastRow ? lastElementRef : null}
                        className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                      >
                        <td className="px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-3.5 font-sans">
                          <span
                            className="font-medium text-xs px-2 py-1 rounded-lg border uppercase
                            bg-slate-100 text-slate-600 border-slate-200
                            dark:bg-white/5 dark:text-slate-400 dark:border-white/5"
                          >
                            {invoice.type}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 font-sans">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border ${getStatusBadgeStyles(
                              invoice.status
                            )}`}
                          >
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right font-semibold text-amber-600 dark:text-amber-500">
                          {(invoice.totalAmount || 0).toFixed(2)}
                        </td>
                        {(selectedType === "TRANSFER" ||
                          selectedType === "ALL") && (
                          <>
                            <td className="px-4 py-3.5 font-sans text-slate-700 dark:text-slate-300">
                              {invoice.branchName || "—"}
                            </td>
                            <td className="px-4 py-3.5 font-sans text-slate-700 dark:text-slate-300">
                              {invoice.destinationBranchName || "—"}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3.5 text-right font-sans text-slate-500 dark:text-slate-400 truncate max-w-[250px]">
                          {invoice.note || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-center font-sans">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (activeMenuId === invoice.id) {
                                setActiveMenuId(null);
                              } else {
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                setMenuAnchor({
                                  top: rect.bottom + window.scrollY + 6,
                                  left: rect.left + window.scrollX - 156,
                                });
                                setActiveInvoice(invoice);
                                setActiveMenuId(invoice.id);
                              }
                            }}
                            className="ellipsis-trigger-btn p-2 rounded-xl border transition
                              bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-500 hover:text-slate-900
                              dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/5 dark:text-slate-400 dark:hover:text-white"
                          >
                            <FaEllipsisV className="text-xs pointer-events-none" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {isLoading && (
            <div
              className="p-8 flex items-center justify-center gap-3 text-xs border-t
              bg-slate-50 text-slate-500 border-slate-200
              dark:bg-slate-950/20 dark:text-slate-400 dark:border-white/5"
            >
              <FaSpinner className="animate-spin text-amber-500 text-base" />
              <span>Loading movement ledger data...</span>
            </div>
          )}

          {!hasMore && invoices.length > 0 && (
            <div
              className="p-4 text-center text-[11px] border-t font-sans italic
              text-slate-400 bg-slate-50 border-slate-200
              dark:text-slate-600 dark:bg-slate-950/40 dark:border-white/5"
            >
              All records synchronized.
            </div>
          )}
        </div>
      </div>

      {/* FLOATING ACTION MENU */}
      {activeMenuId !== null &&
        (() => {
          const workflow = getWorkflow(activeInvoice);
          return (
            <div
              ref={menuContainerRef}
              className="fixed w-52 rounded-xl border shadow-2xl p-1.5 z-[999] space-y-0.5
                bg-white border-slate-200
                dark:bg-slate-900 dark:border-white/10 dark:bg-opacity-95 backdrop-blur-xl"
              style={{ top: menuAnchor.top, left: menuAnchor.left }}
            >
              <button
                type="button"
                onClick={() => handleOpenActionModal(activeInvoice)}
                className={menuButtonClass}
              >
                {workflow.canEdit ? (
                  <>
                    <FaEdit className="text-amber-500 dark:text-amber-400" />
                    <span>View / Edit Record</span>
                  </>
                ) : (
                  <>
                    <FaEye className="text-slate-400" />
                    <span>View Record (Read-Only)</span>
                  </>
                )}
              </button>

              {workflow.canFinalize && (
                <button
                  type="button"
                  onClick={() => handleFinalize(activeInvoice.id)}
                  className={`${menuButtonClass} hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10`}
                >
                  <FaCheckCircle className="text-slate-400" />
                  <span>Approve & Post</span>
                </button>
              )}
            </div>
          );
        })()}

      <InvoiceModal
        isOpen={isModalOpen}
        invoice={activeInvoice}
        initialType={activeInvoice?.type || getCreationTypeFallback()}
        onClose={() => {
          setIsModalOpen(false);
          setActiveInvoice(null);
        }}
        onSaveSuccess={refreshRegistryGrid}
      />
    </div>
  );
};

export default InventoryMovement;
